import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";
import BottomNav from "../components/BottomNav";
import AdminTab from "./AdminTab";
import RacesTab from "./RacesTab";
import MyPicksTab from "./MyPicksTab";
import AllPicksTab from "./AllPicksTab";
import LadderTab from "./LadderTab";
import ComingSoonTab from "./ComingSoonTab";
import Loading from "./Loading";
import { toast } from "sonner";

export default function AppShell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [tab, setTab] = useState("races");
  const [loadingGroups, setLoadingGroups] = useState(true);

  useEffect(() => {
    if (user && !user.profile_complete) {
      nav("/setup", { replace: true });
    }
  }, [user, nav]);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const { data } = await api.get("/groups");
      setGroups(data.groups);
      setActiveGroupId((cur) => {
        if (cur && data.groups.find((g) => g.id === cur)) return cur;
        return data.groups[0]?.id || null;
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load groups"));
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const activeGroup = groups.find((g) => g.id === activeGroupId) || null;
  const isOwner = user?.role === "owner";

  const onGroupCreated = (group) => {
    setGroups((prev) => [...prev, group]);
    setActiveGroupId(group.id);
  };

  const onGroupUpdated = (group) => {
    setGroups((prev) => prev.map((g) => (g.id === group.id ? group : g)));
  };

  if (loadingGroups) return <Loading label="Loading your groups…" />;

  return (
    <div className="min-h-screen w-full bg-[#f8f9fa]">
      <AppHeader
        groups={groups}
        activeGroupId={activeGroupId}
        onSelectGroup={setActiveGroupId}
      />
      <main className="max-w-[480px] mx-auto pt-16 pb-24" data-testid="app-main">
        {tab === "races" && <RacesTab group={activeGroup} />}
        {tab === "my-picks" && <MyPicksTab group={activeGroup} />}
        {tab === "all-picks" && <AllPicksTab group={activeGroup} />}
        {tab === "admin" && (
          <AdminTab
            group={activeGroup}
            isOwner={isOwner}
            onGroupCreated={onGroupCreated}
            onGroupUpdated={onGroupUpdated}
          />
        )}
        {tab === "ladder" && <LadderTab group={activeGroup} />}
      </main>
      <BottomNav active={tab} onChange={setTab} />
    </div>
  );
}
